import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articleIdMap, getRelatedArticles } from '@/mocks/articles';

const articleDetailImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Modern%20technology%20platform%20upgrade%20concept%20with%20server%20room%20and%20digital%20interfaces%2C%20professional%20atmosphere%2C%20warm%20lighting%2C%20clean%20minimal%20design&width=1200&height=600&seq=article-detail-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Official%20certification%20and%20authorization%20seal%20on%20clean%20desk%20with%20warm%20lighting%2C%20professional%20legal%20atmosphere%2C%20trust%20and%20security%20theme&width=1200&height=600&seq=article-detail-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Customer%20service%20team%20working%20in%20modern%20office%20with%20headsets%2C%20professional%20support%20environment%2C%20warm%20friendly%20atmosphere&width=1200&height=600&seq=article-detail-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Future%20technology%20trends%20with%20digital%20transformation%20concept%2C%20modern%20data%20visualization%2C%20clean%20futuristic%20atmosphere&width=1200&height=600&seq=article-detail-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Cybersecurity%20shield%20and%20data%20protection%20concept%20with%20digital%20lock%2C%20modern%20security%20technology%2C%20professional%20atmosphere&width=1200&height=600&seq=article-detail-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Happy%20user%20reviewing%20on%20laptop%20in%20cozy%20cafe%20with%20warm%20natural%20lighting%2C%20satisfied%20customer%20experience%2C%20clean%20minimal%20scene&width=1200&height=600&seq=article-detail-6&orientation=landscape',
};

const relatedArticleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Server%20room%20technology%20upgrade%2C%20modern%20data%20center%2C%20professional%20IT%20environment%2C%20clean%20minimal&width=400&height=280&seq=rel-art-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Certificate%20and%20official%20documents%20on%20desk%2C%20legal%20compliance%20theme%2C%20professional%20atmosphere&width=400&height=280&seq=rel-art-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Professional%20support%20team%20in%20call%20center%2C%20modern%20office%2C%20customer%20service%20excellence&width=400&height=280&seq=rel-art-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Digital%20transformation%20concept%20with%20AI%20and%20data%2C%20modern%20technology%2C%20clean%20futuristic%20design&width=400&height=280&seq=rel-art-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Data%20security%20shield%20with%20encryption%20icons%2C%20cybersecurity%20protection%2C%20professional%20modern%20theme&width=400&height=280&seq=rel-art-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Customer%20review%20and%20testimonial%20concept%2C%20happy%20user%20experience%2C%20warm%20professional%20atmosphere&width=400&height=280&seq=rel-art-6&orientation=landscape',
};

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const article = id ? articleIdMap[id] : null;
  const relatedArticles = id ? getRelatedArticles(id) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-article-line text-2xl" />
            </div>
            <p className="text-foreground-600">文章未找到</p>
            <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              <i className="ri-arrow-left-line" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumb.home'), item: 'https://www.kaiyun.com' },
          { '@type': 'ListItem', position: 2, name: t('nav.articles'), item: 'https://www.kaiyun.com/article/article-1' },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        datePublished: article.publishDate,
        author: { '@type': 'Organization', name: t('schema.org_name') },
        publisher: { '@type': 'Organization', name: t('schema.org_name') },
        url: `https://www.kaiyun.com/article/${article.id}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: t('nav.articles'), to: '/article/article-1' },
            { label: article.title },
          ]}
        />

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
              {/* Main Content */}
              <div className="flex-1 max-w-3xl">
                <div className="mb-6">
                  <span className="inline-block text-xs font-medium text-accent-700 bg-accent-100 px-3 py-1 rounded-full mb-4">
                    {article.category}
                  </span>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1.5">
                      <i className="ri-calendar-line" />
                      {article.publishDate}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <i className="ri-time-line" />
                      {article.readTime}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
                  <img
                    src={articleDetailImages[article.id] || ''}
                    alt={article.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                <div className="mt-10 pt-8 border-t border-background-200/70">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <Link
                      to="/article/article-1"
                      className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line" />
                      {t('article.back')}
                    </Link>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-500">{t('article.share')}：</span>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-wechat-line text-sm" />
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-weibo-line text-sm" />
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-links-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <aside className="lg:w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-5 pb-3 border-b border-background-200/70">
                    {t('article.related')}
                  </h3>
                  <div className="space-y-4">
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        to={`/article/${rel.id}`}
                        className="flex gap-3 group cursor-pointer"
                      >
                        <div className="w-20 h-16 flex-shrink-0 rounded-md overflow-hidden">
                          <img
                            src={relatedArticleImages[rel.id] || ''}
                            alt={rel.title}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                            {rel.title}
                          </h4>
                          <span className="text-xs text-foreground-400">{rel.publishDate}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}